import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { assertNotImpersonating } from '../utils/impersonationGuard';

export interface UserProfile {
  id?: string;
  email: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  website?: string;
  howDidYouHear?: string;
  companyName?: string;
  // New onboarding flow properties
  profileCompleted?: boolean;
  profileCompletedAt?: Timestamp;
  workspaceName?: string;
  domain?: string;
  workspaceCompleted?: boolean;
  workspaceCompletedAt?: Timestamp;
  defaultWorkspaceId?: string;
  // Legacy onboarding properties (kept for backward compatibility)
  onboardingCompleted?: boolean;
  onboardingCompletedAt?: Timestamp;
  emailMarketingExperience?: string;
  previousPlatform?: string;
  surveyCompleted?: boolean;
  surveyCompletedAt?: Timestamp;
  industry?: string;
  companyInfoCompleted?: boolean;
  companyInfoCompletedAt?: Timestamp;
  address?: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    region?: string;
    postalCode?: string;
    country: string;
    keepPrivate: boolean;
  };
  addressCompleted?: boolean;
  addressCompletedAt?: Timestamp;
  branding?: {
    logos: string[];
    primaryColor: string;
    secondaryColors: string[];
    headingFont: string;
    headingFontWeight: 'normal' | 'bold';
    headingFontStyle: 'normal' | 'italic';
    bodyFont: string;
    bodyFontWeight: 'normal' | 'bold';
    bodyFontStyle: 'normal' | 'italic';
    socialLinks: Array<{
      platform: string;
      url: string;
    }>;
  };
  brandingCompleted?: boolean;
  brandingCompletedAt?: Timestamp;
  subscription?: {
    plan: 'starter' | 'pro';
    subscribers: number;
    emails: number;
    currency: string;
    status: 'active' | 'inactive' | 'cancelled' | 'past_due' | 'unpaid';
    startDate: Timestamp;
    endDate?: Timestamp;
    // Stripe integration fields
    stripeCustomerId?: string;
    stripeSubscriptionId?: string;
    currentPeriodStart?: Timestamp;
    currentPeriodEnd?: Timestamp;
    cancelAtPeriodEnd?: boolean;
    // Billing fields
    lastPaymentDate?: Timestamp;
    nextBillingDate?: Timestamp;
    totalSpent?: number;
  };
  planSelected?: boolean;
  planSelectedAt?: Timestamp;
  /** Set when admin + founder registration emails were sent (dashboard milestone). */
  registrationEmailsSentAt?: Timestamp;
  welcomeCompleted?: boolean;
  welcomeCompletedAt?: Timestamp;
  onboardingWalkthroughCompleted?: boolean;
  onboardingWalkthroughCompletedAt?: Timestamp;
  timezone?: string;
  phoneNumber?: string;
  profilePictureUrl?: string;
  emailPreferences?: {
    tips: boolean;
    news: boolean;
    receipts: boolean;
  };
  /** App Store install uses Shopify Billing; web signup uses direct gateways. */
  billingSource?: 'shopify' | 'direct';
  acquisitionChannel?: 'shopify_app_store' | 'web' | string;
  /** Privy-style install wizard finished (all steps including sync). */
  shopifyInstallWizardCompleted?: boolean;
  shopifyInstallWizardCompletedAt?: Timestamp;
  /** Skip email passcode when merchant confirmed email during Shopify install. */
  emailTrustedViaShopify?: boolean;
  signupGoal?: 'grow_list' | 'newsletters' | 'automations' | 'browse';
  previousEmailTool?: string;
  annualRevenueBand?: string;
  referralChannel?: string;
  // Shopify and Sync fields
  shopifyStore?: string;
  shopifySyncSettings?: {
    syncShopifyToScubamail: boolean;
    syncScubamailToShopify: boolean;
    syncGroup: 'all' | 'existing';
    syncNameEmail: boolean;
    syncMarketingStatus: boolean;
    syncEvents: boolean;
    syncCustomProperties: boolean;
    completedAt: Timestamp;
  };
  syncCompleted?: boolean;
  syncCompletedAt?: Timestamp;

  referralCode?: string; // Unique code for this user's referral link
  referredByCode?: string; // Code of the user who referred them
  referredByUserId?: string; // User ID of the referrer
  referralEarnings?: number; // Total earnings from referrals (in USD)
  referralClicks?: number; // Total clicks on referral link
  referralCredits?: number; // Available credits to use (in USD)
  referralCreditsUsed?: number; // Total credits used (in USD)
  createdAt: Timestamp;
  lastLoginAt: Timestamp;
  updatedAt?: Timestamp;
}

export interface UserSettings {
  id?: string;
  userId: string;
  companyName?: string;
  websiteUrl?: string;
  accountDomain?: string;
  defaultLandingPage?: string;
  industry?: string;
  address?: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    region?: string;
    postalCode?: string;
    country: string;
    keepPrivate: boolean;
  };
  branding?: {
    logos: string[];
    primaryColor: string;
    secondaryColors: string[];
    headingFont: string;
    headingFontWeight: 'normal' | 'bold';
    headingFontStyle: 'normal' | 'italic';
    bodyFont: string;
    bodyFontWeight: 'normal' | 'bold';
    bodyFontStyle: 'normal' | 'italic';
    socialLinks: Array<{
      platform: string;
      url: string;
    }>;
  };
  subscription?: {
    plan: 'starter' | 'pro';
    subscribers: number;
    emails: number;
    currency: string;
    status: 'active' | 'inactive' | 'cancelled' | 'past_due' | 'unpaid';
    startDate: Timestamp;
    endDate?: Timestamp;
    // Stripe integration fields
    stripeCustomerId?: string;
    stripeSubscriptionId?: string;
    currentPeriodStart?: Timestamp;
    currentPeriodEnd?: Timestamp;
    cancelAtPeriodEnd?: boolean;
    // Billing fields
    lastPaymentDate?: Timestamp;
    nextBillingDate?: Timestamp;
    totalSpent?: number;
  };
  theme?: 'light' | 'dark';
  language?: string;
  notifications?: {
    email: boolean;
    push: boolean;
    sms: boolean;
  };
  privacy?: {
    profileVisibility: 'public' | 'private';
    dataSharing: boolean;
  };
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface BrandingSettings {
  id?: string;
  userId: string;
  logos?: {
    id: string;
    url: string;
    name: string;
    isDefault: boolean;
    uploadedAt: Timestamp | Date;
  }[];
  colors?: {
    primary: string;
    secondary: string[];
  };
  typography?: {
    heading: {
      font: string;
      size: string;
      bold: boolean;
      italic: boolean;
    };
    body: {
      font: string;
      size: string;
      bold: boolean;
      italic: boolean;
    };
  };
  socialLinks?: {
    id: string;
    platform: string;
    url: string;
    icon: string;
    order: number;
  }[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface ActivityDocument {
  id?: string;
  userId: string;
  workspaceId?: string;
  action: string;
  type?: string;
  description?: string;
  details: string;
  timestamp: Timestamp;
}

export interface ContactField {
  id?: string;
  userId: string;
  label: string;
  type: 'Text' | 'Date' | 'Number' | 'Choice (select one)' | 'Choice (select many)';
  mergeTag: string;
  defaultValue: string;
  choices?: string[];
  isRequired: boolean;
  isVisible: boolean;
  order: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}


// Activity operations
export const activityService = {
  // Log an activity event
  logActivity: async (activity: Omit<ActivityDocument, 'id'>) => {
    await addDoc(collection(db, 'activities'), activity);
  },

  // Get recent activities for a user
  getUserActivities: async (userId: string, limitCount = 50) => {
    const q = query(
      collection(db, 'activities'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as ActivityDocument[];
  },

  // Get recent activities for a workspace
  getWorkspaceActivities: async (workspaceId: string, limitCount = 10) => {
    const q = query(
      collection(db, 'activities'),
      where('workspaceId', '==', workspaceId),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as ActivityDocument[];
  },

};


// Collection operations
export const collectionService = {
  // Create a new collection by adding a document
  createCollection: async (collectionName: string, documentData: any, documentId?: string) => {
    const collectionRef = collection(db, collectionName);

    if (documentId) {
      // Create with specific document ID
      const docRef = doc(collectionRef, documentId);
      await setDoc(docRef, {
        ...documentData,
        createdAt: Timestamp.now()
      });
      return docRef.id;
    } else {
      // Create with auto-generated ID
      const docRef = await addDoc(collectionRef, {
        ...documentData,
        createdAt: Timestamp.now()
      });
      return docRef.id;
    }
  },

  // Add document to existing collection
  addDocument: async (collectionName: string, documentData: any) => {
    const collectionRef = collection(db, collectionName);
    const docRef = await addDoc(collectionRef, {
      ...documentData,
      createdAt: Timestamp.now()
    });
    return docRef.id;
  },

  // Get all documents from a collection
  getCollection: async (collectionName: string) => {
    const collectionRef = collection(db, collectionName);
    const querySnapshot = await getDocs(collectionRef);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Get documents with conditions
  getCollectionWithQuery: async (collectionName: string, field: string, operator: any, value: any) => {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, where(field, operator, value));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }
};

// User settings operations
export const userSettingsService = {
  // Create or update user settings
  createUserSettings: async (userId: string, settingsData: Partial<UserSettings>) => {
    const now = Timestamp.now();
    const settings = {
      userId,
      ...settingsData,
      createdAt: now,
      updatedAt: now
    };
    const docRef = doc(db, 'user_settings', userId);
    await setDoc(docRef, settings, { merge: true });
    return docRef.id;
  },

  // Get user settings
  getUserSettings: async (userId: string) => {
    const docRef = doc(db, 'user_settings', userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as UserSettings;
    }
    return null;
  },

  // Update user settings
  updateUserSettings: async (userId: string, settingsData: Partial<UserSettings>) => {
    const docRef = doc(db, 'user_settings', userId);

    // First get the existing document to preserve other fields
    const existingDoc = await getDoc(docRef);
    const existingData = existingDoc.exists() ? existingDoc.data() : {};

    // Merge the new settings data with existing data
    const updatedData = {
      ...existingData,
      ...settingsData,
      updatedAt: Timestamp.now()
    };

    await setDoc(docRef, updatedData, { merge: true });
  },

  // Delete user settings
  deleteUserSettings: async (userId: string) => {
    const docRef = doc(db, 'user_settings', userId);
    await deleteDoc(docRef);
  }
};

// Branding settings operations
export const brandingService = {
  // Create or update branding settings
  createBrandingSettings: async (userId: string, brandingData: Partial<BrandingSettings>) => {
    const now = Timestamp.now();
    const branding = {
      userId,
      ...brandingData,
      createdAt: now,
      updatedAt: now
    };
    const docRef = doc(db, 'branding_settings', userId);
    await setDoc(docRef, branding, { merge: true });
    return docRef.id;
  },

  // Get branding settings
  getBrandingSettings: async (userId: string) => {
    const docRef = doc(db, 'branding_settings', userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as BrandingSettings;
    }
    return null;
  },

  // Update branding settings
  updateBrandingSettings: async (userId: string, brandingData: Partial<BrandingSettings>) => {
    const docRef = doc(db, 'branding_settings', userId);

    // First get the existing document to preserve other fields
    const existingDoc = await getDoc(docRef);
    const existingData = existingDoc.exists() ? existingDoc.data() : {};

    // Merge the new branding data with existing data
    const updatedData = {
      ...existingData,
      ...brandingData,
      updatedAt: Timestamp.now()
    };

    await setDoc(docRef, updatedData, { merge: true });
  },

  // Delete branding settings
  deleteBrandingSettings: async (userId: string) => {
    const docRef = doc(db, 'branding_settings', userId);
    await deleteDoc(docRef);
  }
};

// User profile operations
export const userService = {
  // Create or update user profile
  createUserProfile: async (userId: string, email: string, displayName?: string) => {
    const now = Timestamp.now();
    const userData = {
      email,
      displayName,
      createdAt: now,
      lastLoginAt: now
    };
    const docRef = doc(db, 'users', userId);
    await setDoc(docRef, userData, { merge: true });
  },

  // Get user profile
  getUserProfile: async (userId: string) => {
    const docRef = doc(db, 'users', userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as UserProfile;
    }
    return null;
  },

  // Update user profile details
  // Update user profile details
  updateUserProfile: async (userId: string, profileData: Partial<UserProfile>) => {
    const docRef = doc(db, 'users', userId);

    // First get the existing document to preserve other fields
    const existingDoc = await getDoc(docRef);
    const existingData = existingDoc.exists() ? existingDoc.data() : {};

    // Merge the new profile data with existing data
    const updatedData = {
      ...existingData,
      ...profileData,
      updatedAt: Timestamp.now()
    };

    await setDoc(docRef, updatedData, { merge: true });
  },

  // Update last login
  updateLastLogin: async (userId: string) => {
    const docRef = doc(db, 'users', userId);
    await updateDoc(docRef, {
      lastLoginAt: Timestamp.now()
    });
  }
};

// Contact field operations
export const contactFieldService = {
  // Create a new contact field
  createContactField: async (fieldData: Omit<ContactField, 'id' | 'createdAt' | 'updatedAt'>) => {
    // Outside the try/catch so the read-only error isn't mistaken for a
    // permission problem and silently written to localStorage instead.
    assertNotImpersonating('create contact field');
    try {
      const now = Timestamp.now();
      const field = {
        ...fieldData,
        createdAt: now,
        updatedAt: now
      };
      const docRef = await addDoc(collection(db, 'contact_fields'), field);
      return docRef.id;
    } catch (error: any) {
      console.error('Error in createContactField:', error);
      // If it's a permission error, store locally as fallback
      if (error.code === 'permission-denied' || error.message?.includes('permission')) {
        console.log('Permission denied, storing field locally as fallback');
        return await contactFieldService.createContactFieldLocally(fieldData);
      }
      // For other errors, re-throw with a generic message
      throw new Error('Failed to create contact field. Please try again.');
    }
  },

  // Create contact field locally as fallback
  createContactFieldLocally: async (fieldData: Omit<ContactField, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const now = Timestamp.now();
      const field = {
        ...fieldData,
        id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        createdAt: now,
        updatedAt: now
      };

      // Store in localStorage
      const localFields = JSON.parse(localStorage.getItem('contact_fields') || '[]');
      localFields.push(field);
      localStorage.setItem('contact_fields', JSON.stringify(localFields));

      console.log('Field stored locally:', field.id);
      return field.id;
    } catch (error) {
      console.error('Error storing field locally:', error);
      throw new Error('Failed to create contact field locally.');
    }
  },

  // Get all contact fields for a user
  getContactFields: async (userId: string) => {
    try {
      const q = query(
        collection(db, 'contact_fields'),
        where('userId', '==', userId),
        orderBy('order', 'asc')
      );
      const querySnapshot = await getDocs(q);
      const firestoreFields = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as ContactField[];

      // Also get locally stored fields
      const localFields = contactFieldService.getLocalContactFields(userId);

      // Combine and sort by order
      const allFields = [...firestoreFields, ...localFields].sort((a, b) => a.order - b.order);

      return allFields;
    } catch (error: any) {
      console.error('Error in getContactFields:', error);
      // If it's a permission error, return local fields only
      if (error.code === 'permission-denied' || error.code === 'unavailable' || error.message?.includes('permission')) {
        console.log('Permission denied, returning local fields only');
        return contactFieldService.getLocalContactFields(userId);
      }
      // For other errors, also return local fields
      console.log('Unknown error in getContactFields, returning local fields:', error.message);
      return contactFieldService.getLocalContactFields(userId);
    }
  },

  // Get locally stored contact fields
  getLocalContactFields: (userId: string) => {
    try {
      const localFields = JSON.parse(localStorage.getItem('contact_fields') || '[]');
      return localFields.filter((field: ContactField) => field.userId === userId);
    } catch (error) {
      console.error('Error getting local contact fields:', error);
      return [];
    }
  },

  // Update a contact field
  updateContactField: async (fieldId: string, fieldData: Partial<ContactField>) => {
    assertNotImpersonating('update contact field');
    const docRef = doc(db, 'contact_fields', fieldId);
    await updateDoc(docRef, {
      ...fieldData,
      updatedAt: Timestamp.now()
    });
  },

  // Delete a contact field
  deleteContactField: async (fieldId: string) => {
    assertNotImpersonating('delete contact field');
    const docRef = doc(db, 'contact_fields', fieldId);
    await deleteDoc(docRef);
  },

  // Reorder contact fields
  reorderContactFields: async (userId: string, fieldOrders: { fieldId: string; order: number }[]) => {
    const batch = fieldOrders.map(({ fieldId, order }) =>
      updateDoc(doc(db, 'contact_fields', fieldId), {
        order,
        updatedAt: Timestamp.now()
      })
    );
    await Promise.all(batch);
  }
};
